import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faChartBar, faDownload, faEuroSign, faSpinner, faUsers } from '@fortawesome/free-solid-svg-icons';
import { bookingsApi } from '@api/api';
import { BookingDto, BookingSummaryDto } from '@api/generated';

const formatAmt = (n: number) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n);

const MonthlyOverviewPage: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [summary, setSummary] = useState<BookingSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const breadcrumb = [{ label: 'Reporty' }, { label: 'Měsíční přehledy' }];

  const monthOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    for (let i = 0; i < 12; i++) {
      const m = dayjs().subtract(i, 'month');
      opts.push({ value: m.format('YYYY-MM'), label: m.format('MMMM YYYY') });
    }
    return opts;
  }, []);

  useEffect(() => {
    if (!entityId) return;
    const start = dayjs(`${selectedMonth}-01`);
    const dateFrom = start.startOf('month').format('YYYY-MM-DD');
    const dateTo = start.endOf('month').format('YYYY-MM-DD');
    setLoading(true);
    bookingsApi
      .getBookingSummary({ salonId: entityId, dateFrom, dateTo })
      .then(res => setSummary(res.data))
      .catch(err => setError(err?.message ?? 'Chyba načítání'))
      .finally(() => setLoading(false));
  }, [entityId, selectedMonth]);

  const totalRevenue = summary?.totalRevenue ?? 0;
  const totalBookings = summary?.totalBookings ?? 0;
  const newCustomers = summary?.newCustomers ?? 0;
  const avgPrice = totalBookings > 0 ? totalRevenue / totalBookings : 0;

  const dailyBreakdown = useMemo(() => {
    const map = new Map<string, { revenue: number; services: number; customers: Set<string> }>();
    (summary?.bookings ?? []).forEach(b => {
      const day = dayjs(b.start).format('YYYY-MM-DD');
      const entry = map.get(day) ?? { revenue: 0, services: 0, customers: new Set<string>() };
      const lineTotal = (b.bookingLines ?? []).reduce((acc, l) => acc + (l.price ?? 0), 0);
      entry.revenue += lineTotal || b.totalAmount || 0;
      entry.services += b.bookingLines?.length ?? 1;
      if (b.customer?.id) entry.customers.add(b.customer.id);
      map.set(day, entry);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, v]) => ({
        date,
        revenue: v.revenue,
        services: v.services,
        customers: v.customers.size,
        day: dayjs(date).locale('cs').format('dd'),
      }));
  }, [summary]);

  const serviceCategories = useMemo(() => {
    const list = [...(summary?.revenueByService ?? [])].sort((a, b) => b.revenue - a.revenue);
    const total = list.reduce((acc, s) => acc + s.revenue, 0) || 1;
    return list.map(s => ({ ...s, percentage: (s.revenue / total) * 100 }));
  }, [summary]);

  const staffPerformance = useMemo(() => {
    const list = [...(summary?.topWorkers ?? [])];
    const total = list.reduce((acc, s) => acc + s.revenue, 0) || 1;
    return list.map(s => ({ ...s, percentage: (s.revenue / total) * 100 }));
  }, [summary]);

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Měsíční přehledy" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Měsíční přehledy</h1>
                <p className="text-gray-600 mt-1">Detailní analýza výkonnosti za měsíc</p>
              </div>
              <div className="flex items-center space-x-3">
                <select
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  {monthOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error}</div>}

          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card icon={faEuroSign} colorBg="bg-green-50" colorIcon="text-green-600" title="Celkové tržby" value={formatAmt(totalRevenue)} loading={loading} />
            <Card icon={faCalendar} colorBg="bg-blue-50" colorIcon="text-blue-600" title="Počet rezervací" value={totalBookings.toLocaleString('cs-CZ')} loading={loading} />
            <Card icon={faUsers} colorBg="bg-purple-50" colorIcon="text-purple-600" title="Noví zákazníci" value={newCustomers.toLocaleString('cs-CZ')} loading={loading} />
            <Card icon={faChartBar} colorBg="bg-orange-50" colorIcon="text-orange-600" title="Průměrná cena" value={formatAmt(avgPrice)} loading={loading} />
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Denní přehled</h3>
              </div>
              <div className="p-6">
                {dailyBreakdown.length === 0 ? (
                  <p className="text-sm text-gray-500">Žádná data.</p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {dailyBreakdown.map(day => (
                      <div key={day.date} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 text-center">
                            <p className="text-xs text-gray-500">{day.day}</p>
                            <p className="font-bold text-gray-900">{dayjs(day.date).format('DD.MM.')}</p>
                          </div>
                          <div className="text-sm text-gray-600">
                            {day.services} služeb · {day.customers} zákazníků
                          </div>
                        </div>
                        <p className="font-bold text-green-600">{formatAmt(day.revenue)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Služby podle kategorií</h3>
              </div>
              <div className="p-6">
                {serviceCategories.length === 0 ? (
                  <p className="text-sm text-gray-500">Žádná data.</p>
                ) : (
                  <div className="space-y-4">
                    {serviceCategories.map(c => (
                      <div key={c.serviceId} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">{c.serviceName}</span>
                          <div className="text-right">
                            <span className="text-sm font-bold text-gray-900">{formatAmt(c.revenue)}</span>
                            <span className="text-xs text-gray-500 ml-2">({c.bookingCount} služeb)</span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${c.percentage}%` }} />
                        </div>
                        <div className="text-xs text-gray-500">{c.percentage.toFixed(1)}% z tržeb</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>

          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Výkonnost zaměstnanců</h3>
            </div>
            <div className="p-6">
              {staffPerformance.length === 0 ? (
                <p className="text-sm text-gray-500">Žádná data.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {staffPerformance.map(s => (
                    <div key={s.workerId} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <FontAwesomeIcon icon={faUsers} className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{s.workerName}</h4>
                          <p className="text-sm text-gray-600">{s.bookingCount} rezervací</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Tržby:</span>
                          <span className="font-bold text-green-600">{formatAmt(s.revenue)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Podíl:</span>
                          <span className="font-medium text-gray-900">{s.percentage.toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div className="bg-green-600 h-1.5 rounded-full" style={{ width: `${s.percentage}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

interface CardProps {
  icon: any;
  colorBg: string;
  colorIcon: string;
  title: string;
  value: string;
  loading?: boolean;
}

const Card: React.FC<CardProps> = ({ icon, colorBg, colorIcon, title, value, loading }) => (
  <div className="bg-white rounded-lg shadow-sm p-6">
    <div className="flex items-center">
      <div className={`p-3 rounded-lg ${colorBg}`}>
        <FontAwesomeIcon icon={icon} className={`text-xl ${colorIcon}`} />
      </div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <div className="flex items-center">
          {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 mr-2" />}
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  </div>
);

export default MonthlyOverviewPage;
